import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { api, eur, imgW } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export function SearchOverlay({ open, onClose }) {
  const { t, lang } = useStore();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => { if (open) setTimeout(() => ref.current?.focus(), 80); else { setQ(""); setRes([]); } }, [open]);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return; }
    const id = setTimeout(() => api.get("/search/suggest", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 220);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/shop?q=${encodeURIComponent(q.trim())}`); onClose(); } };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-sun/95 backdrop-blur-xl" data-testid="search-overlay">
          <div className="container-x pt-10">
            <div className="flex justify-end"><button onClick={onClose} aria-label="Close search" data-testid="search-close-btn"><X className="h-7 w-7" /></button></div>
            <form onSubmit={submit} className="mt-8 flex items-center border-b-2 border-ink pb-3">
              <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchPh")} data-testid="search-input"
                className="w-full bg-transparent font-display text-3xl font-bold uppercase tracking-tight outline-none placeholder:text-ink/20 sm:text-5xl" />
              <button type="submit" aria-label="Search" data-testid="search-submit-btn"><ArrowRight className="h-8 w-8" /></button>
            </form>
            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
              {res.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to={`/product/${p.slug}`} onClick={onClose} className="group block" data-testid={`search-result-${i}`}>
                    <div className="aspect-square overflow-hidden bg-white">
                      <img src={imgW(p.images[0], 500)} alt={p.name} className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <p className="mt-3 text-xs text-ink/50">{p.brand}</p>
                    <p className="line-clamp-1 text-sm font-medium">{p.name}</p>
                    <p className="text-sm">{eur(p.final_price, lang)}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
            {!q && (
              <div className="mt-4 flex flex-wrap gap-3">
                {["Air Jordan 4", "Dunk Low", "Samba OG", "550", "GEL-Kayano 14", "Air Max 95"].map((s) => (
                  <button key={s} onClick={() => setQ(s)} className="border border-concrete px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors hover:border-ink"
                    data-testid={`search-suggest-${s.replace(/\s/g, "-").toLowerCase()}`}>{s}</button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
